import { createContext, useCallback, useContext, useEffect, useState } from "react";

const ModalContext = createContext(null);

export const useModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return ctx;
};

function ModalDialog({ title, body, footer, size, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      data-testid="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`modal ${size || ""}`} role="dialog" data-testid="modal-dialog">
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" data-testid="modal-close" aria-label="Fermer" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div className="modal-body">{body}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export function ModalProvider({ children }) {
  const [modal, setModal] = useState(null);

  const closeModal = useCallback(() => setModal(null), []);

  const openModal = useCallback(({ title, body, footer, size = "" }) => {
    setModal({ title, body, footer, size });
  }, []);

  const confirm = useCallback(({ title, message, confirmText = "Confirmer", type = "danger", onConfirm }) => {
    const commentRef = { current: null };
    setModal({
      title,
      body: (
        <>
          <p style={{ margin: 0, color: "var(--text-2)" }}>{message}</p>
          <div className="form-row" style={{ marginTop: 14 }}>
            <label className="label">Commentaire (optionnel)</label>
            <textarea
              className="textarea"
              data-testid="confirm-comment"
              placeholder="Ajouter un motif ou commentaire…"
              ref={(el) => {
                commentRef.current = el;
              }}
            />
          </div>
        </>
      ),
      footer: (
        <>
          <button className="btn" data-testid="confirm-cancel" onClick={() => setModal(null)}>
            Annuler
          </button>
          <button
            className={`btn btn-${type}`}
            data-testid="confirm-ok"
            onClick={() => {
              const comment = commentRef.current ? commentRef.current.value : "";
              setModal(null);
              if (onConfirm) onConfirm(comment);
            }}
          >
            {confirmText}
          </button>
        </>
      ),
    });
  }, []);

  return (
    <ModalContext.Provider value={{ openModal, confirm, closeModal }}>
      {children}
      <div className="modal-root" id="modalRoot" data-testid="modal-root">
        {modal ? <ModalDialog {...modal} onClose={closeModal} /> : null}
      </div>
    </ModalContext.Provider>
  );
}

export default ModalProvider;
